import React, { Fragment } from "react";
import { observer } from "mobx-react";
import { http } from "../../services/HttpInterceptor";
import { RouteComponentProps, Link, Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import { observable } from "mobx";
import { PhotoProvider, PhotoView } from "react-photo-view";

interface EditPaymentProps extends RouteComponentProps {
  changeToggle(action?: boolean): void;
}

@observer
export default class EditPayment extends React.Component<EditPaymentProps> {
  @observable private payment: any;
  @observable private redirect = false;
  @observable private isLoaded: boolean = false;
  @observable private selectedFiles: File[] = [];
  @observable private previewUrls: string[] = [];
  @observable private isUploading: boolean = false;
  private fileInput: HTMLInputElement | null = null;
  @observable private web_name: string = (() => {
    if (process.env.NODE_ENV === "development") {
      return "develop";
    }

    const url = process.env.REACT_APP_URL;

    switch (url) {
      case "https://sur.serviciosinalambricos.com.mx":
      case "https://sur-2.serviciosinalambricos.com.mx":
      case "https://servicios-sur.web.app":
        return "sur";
      case "https://norte.serviciosinalambricos.com.mx":
      case "https://norte-2.serviciosinalambricos.com.mx":
        return "norte";
      case "https://wirserv.serviciosinalambricos.com.mx":
      case "https://wirserv-2.serviciosinalambricos.com.mx":
        return "wirserv";
      case "https://develop.serviciosinalambricos.com.mx":
        return "develop";
      default:
        console.warn(`Unexpected REACT_APP_URL in production: ${url}`);
        return "";
    }
  })();

  componentDidMount() {
    this.props.changeToggle(true);
    this.init();
  }

  componentWillUnmount() {
    // Limpiar URLs para evitar memory leaks
    this.previewUrls.forEach(url => URL.revokeObjectURL(url));
  }

  init = () => {
    const match = this.props.match;
    const id = (match as any).params.id;
    http
      .get(`/api/payment/read/${id}`)
      .then((response) => {
        this.payment = response.data;
        this.isLoaded = true;
      })
      .catch(() => {
        toast.error("Error cargando Recibo");
      });
  };

  private handleFileSelection = (files: File[]) => {
    // Limpiar URLs anteriores para evitar memory leaks
    this.previewUrls.forEach(url => URL.revokeObjectURL(url));
    
    // Agregar nuevos archivos a los existentes
    const newFiles = Array.from(files);
    const updatedFiles = [...this.selectedFiles, ...newFiles];
    
    // Crear URLs para los nuevos archivos
    const newUrls = newFiles.map(file => URL.createObjectURL(file));
    
    // Actualizar los arrays
    this.selectedFiles = updatedFiles;
    this.previewUrls = [...this.previewUrls, ...newUrls];
  };

  private removeFile = (index: number) => {
    // Revocar URL para evitar memory leak
    URL.revokeObjectURL(this.previewUrls[index]);
    
    // Remover archivo y URL
    const newFiles = this.selectedFiles.filter((_, i) => i !== index);
    const newUrls = this.previewUrls.filter((_, i) => i !== index);
    
    this.selectedFiles = newFiles;
    this.previewUrls = newUrls;
  };

  private clearAllFiles = () => {
    // Limpiar todas las URLs
    this.previewUrls.forEach(url => URL.revokeObjectURL(url));
    this.selectedFiles = [];
    this.previewUrls = [];
  };

  private uploadAllFiles = async () => {
    if (this.selectedFiles.length === 0) {
      toast.error("No hay imágenes seleccionadas");
      return;
    }

    this.isUploading = true;
    const baseId = new Date().getTime().toString();
    const currentPictureIds = this.payment.picture_id
      ? this.payment.picture_id.split(",").filter((id: string) => id)
      : [];

    try {
      // Crear FormData con todos los archivos
      const formData = new FormData();
      formData.append("id", baseId);
      
      // Agregar todos los archivos con el nombre 'files[]'
      this.selectedFiles.forEach((file, index) => {
        formData.append("files[]", file, file.name);
      });

      const response = await http.post("/api/payment/uploadImage", formData);
      const responseText = response.data || response;
      
      if (responseText.includes("Success:") || responseText.includes("subido(s) correctamente")) {
        // Generar los IDs basados en el baseId y la cantidad de archivos
        const uploadedIds = this.selectedFiles.map((_, index) => `${baseId}_${index}`);
        
        // Agregar los nuevos IDs a los existentes
        const allPictureIds = [...currentPictureIds, ...uploadedIds];
        this.payment.picture_id = allPictureIds.join(",");
        
        // Actualizar en la base de datos
        await http.post(`/api/payment/update`, JSON.stringify(this.payment));
        
        toast.success(`${this.selectedFiles.length} imagen(es) agregada(s) con éxito`);
        
        // Limpiar archivos seleccionados después de subir
        this.clearAllFiles();
      } else {
        throw new Error("Error en la respuesta del servidor");
      }
      
    } catch (error) {
      toast.error("Error subiendo las imágenes");
      console.error("Upload error:", error);
    } finally {
      this.isUploading = false;
    }
  };

  private removeImage = (imageIdToRemove: string): void => {
    // Primero eliminar la imagen del S3
    http
      .post(`/api/payment/delete_image/${this.payment.id}/${imageIdToRemove}`)
      .then(() => {
        // Si se eliminó del S3, quitar el ID de la base de datos
        const currentPictureIds = this.payment.picture_id
          ? this.payment.picture_id.split(",").filter((id: string) => id)
          : [];
        const updatedPictureIds = currentPictureIds.filter(
          (id: string) => id !== imageIdToRemove
        );
        this.payment.picture_id = updatedPictureIds.join(",");

        // Actualizar en la base de datos
        return http.post(`/api/payment/update`, JSON.stringify(this.payment));
      })
      .then(() => {
        toast.success("Imagen eliminada con éxito");
      })
      .catch(() => {
        toast.error("Error eliminando la imagen");
      });
  };

  render() {
    if (!this.isLoaded) {
      return null;
    }
    if (this.redirect) {
      return <Redirect to="/pagos" />;
    }
    return (
      <Fragment>
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header card-header-rose card-header-text">
                <div className="card-text">
                  <h4 className="card-title">Pago: #{this.payment.id}</h4>
                </div>
              </div>
              <div className="card-body ">
                <div className="row">
                  <div className="col-sm-10"></div>
                  <div className="col-sm-2">
                    <Link to={`../../pagos`}>
                      <button className="btn btn-danger btn-sm  pull-right">
                        <i className="material-icons">highlight_off</i>
                        <div className="ripple-container"></div>
                      </button>
                    </Link>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Cantidad</label>
                  <div className="col-sm-8">
                    <strong>${this.payment.amount}</strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Registrado por</label>
                  <div className="col-sm-8">
                    <strong>{this.payment.created_by_name}</strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Registrado el</label>
                  <div className="col-sm-8">
                    <strong>{this.payment.created_date}</strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Recibo #</label>
                  <div className="col-sm-8">
                    <strong>{this.payment.invoice_id}</strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Tipo de Pago</label>
                  <div className="col-sm-8">
                    <strong>{this.payment.type}</strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Direccion</label>
                  <div className="col-sm-8">
                    <strong>{this.payment.address}</strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Folio</label>
                  <div className="col-sm-8">
                    <strong>{this.payment.folio}</strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Tarjeta</label>
                  <div className="col-sm-8">
                    <strong>
                      {this.payment.card
                        ? this.payment.card
                        : this.payment.newcard}
                    </strong>
                  </div>
                </div>
                <div className="row">
                  <label className="col-sm-4">Fecha de deposito</label>
                  <div className="col-sm-8">
                    <strong>{this.payment.date}</strong>
                  </div>
                </div>

                {this.payment.picture_id && (
                  <div className="row">
                    <div className="col-sm-12">
                      <h5>Imágenes actuales:</h5>
                      <PhotoProvider>
                        {this.payment.picture_id
                          .split(",")
                          .filter((id: string) => id && id.trim() !== "")
                          .map((pictureId: string, index: number) => {
                            const cleanId = pictureId.trim();
                            return (
                              <div
                                key={cleanId}
                                style={{
                                  display: "inline-block",
                                  margin: "5px",
                                  position: "relative",
                                }}
                              >
                                <PhotoView
                                  src={`https://storage.googleapis.com/${this.web_name === "norte" ? "servicios-norte" : this.web_name === "wirserv" ? "servicios-wirserv" : this.web_name === "sur" ? "servicios-sur" : "servicios-develop"}/${this.web_name}/payments/${cleanId}.jpg`}
                                >
                                  <img
                                    src={`https://storage.googleapis.com/${this.web_name === "norte" ? "servicios-norte" : this.web_name === "wirserv" ? "servicios-wirserv" : this.web_name === "sur" ? "servicios-sur" : "servicios-develop"}/${this.web_name}/payments/${cleanId}.jpg`}
                                    alt={`Imagen ${index + 1}`}
                                    style={{
                                      width: "150px",
                                      height: "150px",
                                      objectFit: "cover",
                                      cursor: "pointer",
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                </PhotoView>
                                <button
                                  onClick={() => this.removeImage(cleanId)}
                                  style={{
                                    position: "absolute",
                                    top: "5px",
                                    right: "5px",
                                    background: "rgba(255, 0, 0, 0.8)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: "25px",
                                    height: "25px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                  }}
                                  title="Eliminar imagen"
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                      </PhotoProvider>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-sm-12">
                    <h5>Agregar nuevas imágenes:</h5>
                    <div className="form-group bmd-form-group">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => this.handleFileSelection(Array.from(e.target.files || []))}
                        ref={(input) => { this.fileInput = input; }}
                        style={{ display: 'none' }}
                        id="file-input"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => this.fileInput && this.fileInput.click()}
                        style={{ marginBottom: '10px' }}
                      >
                        <i className="material-icons" style={{ fontSize: '16px', marginRight: '5px' }}>add_photo_alternate</i>
                        Seleccionar Imágenes
                      </button>
                      <small className="form-text text-muted" style={{ display: 'block', marginTop: '5px' }}>
                        Selecciona una o múltiples imágenes (JPG, JPEG). Máximo 10MB por imagen.
                      </small>
                    </div>
                  </div>
                </div>
                
                {this.previewUrls.length > 0 && (
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="preview-container" style={{ marginBottom: '15px' }}>
                        <h6>Vista previa de las nuevas imágenes:</h6>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                          {this.previewUrls.map((url, idx) => (
                            <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
                              <img 
                                src={url} 
                                alt={`preview-${idx}`} 
                                style={{ 
                                  width: '100px', 
                                  height: '100px', 
                                  objectFit: 'cover',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }} 
                              />
                              <div style={{ 
                                position: 'absolute', 
                                top: '2px', 
                                right: '2px',
                                background: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                              onClick={() => this.removeFile(idx)}
                              title="Eliminar imagen"
                              >
                                ×
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={this.uploadAllFiles}
                            type="button"
                            className="btn btn-success btn-sm"
                            disabled={this.isUploading}
                            style={{ marginRight: '10px' }}
                          >
                            <i className="material-icons" style={{ fontSize: '16px', marginRight: '5px' }}>
                              {this.isUploading ? 'hourglass_empty' : 'cloud_upload'}
                            </i>
                            {this.isUploading ? 'Subiendo...' : `Subir ${this.selectedFiles.length} Imagen(es)`}
                          </button>
                          <button
                            onClick={this.clearAllFiles}
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={this.isUploading}
                          >
                            <i className="material-icons" style={{ fontSize: '16px', marginRight: '5px' }}>clear_all</i>
                            Limpiar Todo
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}
